import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Truck, 
  Search, 
  Edit2, 
  Trash2, 
  Save,
  X,
  DollarSign,
  Calendar,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Loader2,
  Building2,
  CheckCircle,
  XCircle
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modal de edición para vehículos de renta
function EditRentalModal({ vehicle, onClose, onSave }) {
  const [formData, setFormData] = useState({
    code: vehicle.code || "",
    brand: vehicle.brand || "",
    model_name: vehicle.model_name || "",
    version: vehicle.version || "",
    year: vehicle.year || "",
    rental_price: vehicle.rental_price || "",
    available: vehicle.available !== false
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/rental-vehicles/${vehicle.id}`, formData);
      toast.success("Unidad de renta actualizada correctamente");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Error al actualizar la unidad");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="edit-rental-modal">
      <div className="w-full max-w-lg bg-[#0f1a2b] border border-slate-700 rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Edit2 className="w-5 h-5 text-orange-500" />
            Editar Unidad de Renta
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">
          {/* Clave / CLV */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Clave / CLV</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
              placeholder="Ej: EXC320, RET420, MTRA..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none uppercase font-mono"
              data-testid="edit-rental-code-input"
            />
            <p className="text-xs text-slate-500 mt-1">Código corto único del equipo (se imprime en el PDF de la cotización)</p>
          </div>

          {/* Marca */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Marca</label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => handleChange("brand", e.target.value)}
              placeholder="Ej: Caterpillar, John Deere..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
              data-testid="edit-rental-brand-input"
            />
          </div>

          {/* Modelo */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Modelo</label>
            <input
              type="text"
              value={formData.model_name}
              onChange={(e) => handleChange("model_name", e.target.value)}
              placeholder="Ej: 320D, Retroexcavadora..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
              data-testid="edit-rental-model-input"
            />
          </div>

          {/* Versión */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Versión</label>
            <input
              type="text"
              value={formData.version}
              onChange={(e) => handleChange("version", e.target.value)}
              placeholder="Ej: Serie 3, Heavy Duty..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
              data-testid="edit-rental-version-input"
            />
          </div>

          {/* Año y Precio de Renta en una fila */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Año</label>
              <input
                type="text"
                value={formData.year}
                onChange={(e) => handleChange("year", e.target.value)}
                placeholder="Ej: 2024"
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
                data-testid="edit-rental-year-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Renta Mensual</label>
              <input
                type="text"
                value={formData.rental_price}
                onChange={(e) => handleChange("rental_price", e.target.value)}
                placeholder="Ej: 50000"
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
                data-testid="edit-rental-price-input"
              />
            </div>
          </div>

          {/* Disponibilidad */}
          <div className="flex items-center gap-3">
            <label className="block text-sm text-slate-400">Disponible:</label>
            <button
              type="button"
              onClick={() => handleChange("available", !formData.available)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                formData.available 
                  ? "bg-green-500/20 text-green-400 border border-green-500/30" 
                  : "bg-red-500/20 text-red-400 border border-red-500/30"
              }`}
            >
              {formData.available ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Sí
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  No
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="edit-rental-save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

function DatabaseRentaTab() {
  const { currentBusiness, businessId } = useBusiness();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  
  // Filters
  const [filterBrand, setFilterBrand] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, [businessId]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/rental-vehicles?business_id=${businessId}`);
      if (response.data.success) {
        setVehicles(response.data.vehicles);
      }
    } catch (error) {
      toast.error("Error al cargar unidades de renta");
    } finally {
      setLoading(false);
    }
  };

  const deleteVehicle = async (vehicleId) => {
    if (!window.confirm("¿Eliminar esta unidad de renta?")) return;
    try {
      await axios.delete(`${API}/rental-vehicles/${vehicleId}`);
      toast.success("Unidad de renta eliminada");
      fetchVehicles();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const filteredVehicles = vehicles.filter(v => {
    const matchesSearch = 
      v.model_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.version?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBrand = !filterBrand || v.brand?.toLowerCase().includes(filterBrand.toLowerCase());
    return matchesSearch && matchesBrand;
  });

  const uniqueBrands = [...new Set(vehicles.map(v => v.brand).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Modal de edición */}
      {editingVehicle && (
        <EditRentalModal 
          vehicle={editingVehicle}
          onClose={() => setEditingVehicle(null)}
          onSave={fetchVehicles}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-montserrat font-bold text-white flex items-center gap-3">
          <Truck className="w-7 h-7 text-orange-500" />
          Base de Datos Renta - {currentBusiness?.name || "Unidades"}
          <span className="text-sm font-normal text-slate-400 ml-2">
            ({vehicles.length} unidades)
          </span>
        </h2>
        <button
          onClick={fetchVehicles}
          className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          data-testid="refresh-rental-btn"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Search and Filters */}
      <div className="glass-card rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              placeholder="Buscar por CLV, marca, modelo o versión..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
              data-testid="search-rental-input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 rounded-lg flex items-center gap-2 transition-colors ${
              showFilters ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            <Filter className="w-5 h-5" />
            Filtros
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Marca</label>
              <select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
              >
                <option value="">Todas las marcas</option>
                {uniqueBrands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Vehicles Table */}
      <div className="glass-card rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
            <p className="text-slate-400">Cargando unidades de renta...</p>
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="p-12 text-center">
            <Truck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No hay unidades de renta registradas</p>
            <p className="text-slate-500 text-sm mt-2">
              Las unidades se agregan al subir fichas técnicas en la pestaña "Diseño Renta"
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">CLV</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Marca</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Modelo</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Versión</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Año</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Renta Mensual</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-slate-400">Disponible</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-slate-400">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filteredVehicles.map((vehicle) => (
                  <React.Fragment key={vehicle.id}>
                    <tr className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-4 py-4">
                        {vehicle.code ? (
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded font-mono text-sm font-semibold">
                            {vehicle.code}
                          </span>
                        ) : (
                          <span className="text-slate-600 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-white">{vehicle.brand || "-"}</td>
                      <td className="px-4 py-4 text-white font-medium">{vehicle.model_name}</td>
                      <td className="px-4 py-4">
                        <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-sm">
                          {vehicle.version}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-slate-400">{vehicle.year || "-"}</td>
                      <td className="px-4 py-4">
                        <span className={vehicle.rental_price ? "text-green-400 font-medium" : "text-slate-500"}>
                          {vehicle.rental_price ? `$${parseInt(vehicle.rental_price).toLocaleString('es-MX')}/mes` : "Sin precio"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {vehicle.available !== false ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded text-sm">
                            <CheckCircle className="w-3 h-3" />
                            Sí
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm">
                            <XCircle className="w-3 h-3" />
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setExpandedId(expandedId === vehicle.id ? null : vehicle.id)}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                            title="Ver especificaciones"
                          >
                            {expandedId === vehicle.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditingVehicle(vehicle)}
                            className="p-2 text-slate-400 hover:text-orange-400 hover:bg-slate-700 rounded transition-colors"
                            title="Editar unidad"
                            data-testid={`edit-rental-btn-${vehicle.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteVehicle(vehicle.id)}
                            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors"
                            title="Eliminar"
                            data-testid={`delete-rental-btn-${vehicle.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === vehicle.id && (
                      <tr className="bg-slate-800/20">
                        <td colSpan="8" className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-sm font-medium text-slate-400 mb-2">Especificaciones Técnicas</h4>
                              <ul className="space-y-1">
                                {vehicle.specifications?.map((spec, idx) => (
                                  <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                                    <span className="text-orange-500">•</span>
                                    {spec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {vehicle.vehicle_image && (
                              <div>
                                <h4 className="text-sm font-medium text-slate-400 mb-2">Imagen</h4>
                                <img 
                                  src={vehicle.vehicle_image} 
                                  alt={vehicle.model_name}
                                  className="max-h-32 rounded"
                                />
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DatabaseRentaTab;
